import React from "react";

export type KpiViewMode = "cards" | "details" | "columns";

export type KpisPolarityFilter = "all" | "positive" | "negative";

export type KpisStatusFilter =
  | "all"
  | "on-track"
  | "need-attention"
  | "off-track"
  | "no-value";

export type KpisSortOrder = "default" | "high-to-low" | "low-to-high";

export type KpisAttachmentFilter = "all" | "with" | "without";

export type KpisTypeFilter =
  | "all"
  | "board"
  | "strategic"
  | "monitor"
  | "performance";

export type KpisMeasurementPeriodFilter =
  | "all"
  | "monthly"
  | "quarterly"
  | "annual";

export type KpisFilters = {
  selectedL1: string;
  searchInput: string;
  polarityFilter: KpisPolarityFilter;
  statusFilter: KpisStatusFilter;
  sortOrder: KpisSortOrder;
  attachmentFilter: KpisAttachmentFilter;
  viewMode: KpiViewMode;
  kpiTypeFilter: KpisTypeFilter;
  measurementPeriodFilter: KpisMeasurementPeriodFilter;
};

const DEFAULT_FILTERS: KpisFilters = {
  selectedL1: "all",
  searchInput: "",
  polarityFilter: "all",
  statusFilter: "all",
  sortOrder: "default",
  attachmentFilter: "all",
  viewMode: "columns",
  kpiTypeFilter: "all",
  measurementPeriodFilter: "all",
};

function readStoredFilters(storageKey: string): Partial<KpisFilters> | null {
  if (!storageKey || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<KpisFilters>;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function isPolarityFilter(v: unknown): v is KpisPolarityFilter {
  return v === "all" || v === "positive" || v === "negative";
}

function isStatusFilter(v: unknown): v is KpisStatusFilter {
  return (
    v === "all" ||
    v === "on-track" ||
    v === "need-attention" ||
    v === "off-track" ||
    v === "no-value"
  );
}

function isSortOrder(v: unknown): v is KpisSortOrder {
  return v === "default" || v === "high-to-low" || v === "low-to-high";
}

function isAttachmentFilter(v: unknown): v is KpisAttachmentFilter {
  return v === "all" || v === "with" || v === "without";
}

function isViewMode(v: unknown): v is KpiViewMode {
  return v === "cards" || v === "details" || v === "columns";
}

function isKpiTypeFilter(v: unknown): v is KpisTypeFilter {
  return (
    v === "all" ||
    v === "board" ||
    v === "strategic" ||
    v === "monitor" ||
    v === "performance"
  );
}

function isMeasurementPeriodFilter(
  v: unknown
): v is KpisMeasurementPeriodFilter {
  return v === "all" || v === "monthly" || v === "quarterly" || v === "annual";
}

export function buildKpisFilterStorageKey(
  modelId: string | undefined,
  _period?: string | undefined
): string {
  // Period is intentionally omitted so N-1 / other filters survive period changes.
  return `kpis-filters-v2:${modelId || ""}`;
}

function migrateLegacyKpisFilterStorage(
  storageKey: string,
  modelId: string | undefined,
  period: string | undefined
): Partial<KpisFilters> | null {
  if (typeof sessionStorage === "undefined") return null;
  const existing = readStoredFilters(storageKey);
  if (existing) return existing;

  const prefix = `kpis-filters:${modelId || ""}:`;
  const preferredKey = period ? `${prefix}${period}` : "";
  let best: Partial<KpisFilters> | null = null;

  try {
    if (preferredKey) {
      best = readStoredFilters(preferredKey);
    }
    if (!best) {
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const candidate = readStoredFilters(key);
        if (!candidate) continue;
        // Prefer a non-default division if present.
        if (
          candidate.selectedL1 &&
          candidate.selectedL1 !== "all" &&
          (!best?.selectedL1 || best.selectedL1 === "all")
        ) {
          best = candidate;
        } else if (!best) {
          best = candidate;
        }
      }
    }
    if (best) {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...DEFAULT_FILTERS, ...best })
      );
    }
  } catch {
    return best;
  }
  return best;
}

/** Merge filter overrides into sessionStorage before navigating to the KPIs page. */
export function persistKpisFilters(
  storageKey: string,
  partial: Partial<KpisFilters>
): void {
  if (!storageKey || typeof sessionStorage === "undefined") return;
  const existing = readStoredFilters(storageKey);
  const payload: KpisFilters = {
    ...DEFAULT_FILTERS,
    ...existing,
    ...partial,
  };
  sessionStorage.setItem(storageKey, JSON.stringify(payload));
}

/** Persist KPI list filters across refresh and navigation (sessionStorage). */
export function useKpisFilters(
  storageKey: string,
  options?: {
    defaultViewMode?: KpiViewMode;
    modelId?: string;
    period?: string;
  }
) {
  const defaultViewMode = options?.defaultViewMode ?? DEFAULT_FILTERS.viewMode;
  const modelId = options?.modelId;
  const period = options?.period;

  const readInitial = React.useCallback(
    (): Partial<KpisFilters> | null =>
      readStoredFilters(storageKey) ||
      migrateLegacyKpisFilterStorage(storageKey, modelId, period),
    [storageKey, modelId, period]
  );

  const [selectedL1, setSelectedL1] = React.useState(
    () => readInitial()?.selectedL1 ?? DEFAULT_FILTERS.selectedL1
  );
  const [searchInput, setSearchInput] = React.useState(
    () => readInitial()?.searchInput ?? DEFAULT_FILTERS.searchInput
  );
  const [polarityFilter, setPolarityFilter] = React.useState<KpisPolarityFilter>(
    () => {
      const saved = readInitial()?.polarityFilter;
      return isPolarityFilter(saved) ? saved : DEFAULT_FILTERS.polarityFilter;
    }
  );
  const [statusFilter, setStatusFilter] = React.useState<KpisStatusFilter>(() => {
    const saved = readInitial()?.statusFilter;
    return isStatusFilter(saved) ? saved : DEFAULT_FILTERS.statusFilter;
  });
  const [sortOrder, setSortOrder] = React.useState<KpisSortOrder>(() => {
    const saved = readInitial()?.sortOrder;
    return isSortOrder(saved) ? saved : DEFAULT_FILTERS.sortOrder;
  });
  const [attachmentFilter, setAttachmentFilter] =
    React.useState<KpisAttachmentFilter>(() => {
      const saved = readInitial()?.attachmentFilter;
      return isAttachmentFilter(saved)
        ? saved
        : DEFAULT_FILTERS.attachmentFilter;
    });
  const [viewMode, setViewMode] = React.useState<KpiViewMode>(() => {
    const saved = readInitial()?.viewMode;
    return isViewMode(saved) ? saved : defaultViewMode;
  });
  const [kpiTypeFilter, setKpiTypeFilter] = React.useState<KpisTypeFilter>(() => {
    const saved = readInitial()?.kpiTypeFilter;
    return isKpiTypeFilter(saved) ? saved : DEFAULT_FILTERS.kpiTypeFilter;
  });
  const [measurementPeriodFilter, setMeasurementPeriodFilter] =
    React.useState<KpisMeasurementPeriodFilter>(() => {
      const saved = readInitial()?.measurementPeriodFilter;
      return isMeasurementPeriodFilter(saved)
        ? saved
        : DEFAULT_FILTERS.measurementPeriodFilter;
    });

  React.useEffect(() => {
    const saved =
      readStoredFilters(storageKey) ||
      migrateLegacyKpisFilterStorage(storageKey, modelId, period);
    if (!saved) return;
    if (saved.selectedL1) setSelectedL1(saved.selectedL1);
    if (typeof saved.searchInput === "string") setSearchInput(saved.searchInput);
    if (isPolarityFilter(saved.polarityFilter)) {
      setPolarityFilter(saved.polarityFilter);
    }
    if (isStatusFilter(saved.statusFilter)) setStatusFilter(saved.statusFilter);
    if (isSortOrder(saved.sortOrder)) setSortOrder(saved.sortOrder);
    if (isAttachmentFilter(saved.attachmentFilter)) {
      setAttachmentFilter(saved.attachmentFilter);
    }
    if (isViewMode(saved.viewMode)) setViewMode(saved.viewMode);
    if (isKpiTypeFilter(saved.kpiTypeFilter)) {
      setKpiTypeFilter(saved.kpiTypeFilter);
    }
    if (isMeasurementPeriodFilter(saved.measurementPeriodFilter)) {
      setMeasurementPeriodFilter(saved.measurementPeriodFilter);
    }
    // Only rehydrate when the storage key / model changes — not on period change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- period used only for one-time legacy migrate
  }, [storageKey, modelId]);

  React.useEffect(() => {
    if (!storageKey || typeof sessionStorage === "undefined") return;
    const payload: KpisFilters = {
      selectedL1,
      searchInput,
      polarityFilter,
      statusFilter,
      sortOrder,
      attachmentFilter,
      viewMode,
      kpiTypeFilter,
      measurementPeriodFilter,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  }, [
    storageKey,
    selectedL1,
    searchInput,
    polarityFilter,
    statusFilter,
    sortOrder,
    attachmentFilter,
    viewMode,
    kpiTypeFilter,
    measurementPeriodFilter,
  ]);

  return {
    selectedL1,
    setSelectedL1,
    searchInput,
    setSearchInput,
    polarityFilter,
    setPolarityFilter,
    statusFilter,
    setStatusFilter,
    sortOrder,
    setSortOrder,
    attachmentFilter,
    setAttachmentFilter,
    viewMode,
    setViewMode,
    kpiTypeFilter,
    setKpiTypeFilter,
    measurementPeriodFilter,
    setMeasurementPeriodFilter,
  };
}
